import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Award, ExternalLink, ChevronDown, ChevronUp, FileText, Activity, AlertTriangle, Newspaper, Link as LinkIcon, Share2, Bookmark } from 'lucide-react';
import CommentBox from './CommentBox';
import Comment from './Comment';
import ActionDropdown from '../../components/ActionDropdown/ActionDropdown';
import { Link } from 'react-router-dom';
import { listEvidenceThunk } from '../../redux/posts/postEvidence/postEvidenceActions';
import { savePostThunk, unsavePostThunk } from '../../redux/savedPosts/savedPostsActions';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';

const buildProfilePath = (post) => {
  const raw =
    post?.user?.username ||
    post?.username ||
    String(post?.user?.id || '');
  const slug = raw.trim().replace(/^@/, '').replace(/[:\s]+$/g, '');
  return `/public-profiles/${encodeURIComponent(slug)}`;
};

const formatDistanceToNow = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const ranges = {
    year: 3600 * 24 * 365,
    month: 3600 * 24 * 30,
    week: 3600 * 24 * 7,
    day: 3600 * 24,
    hour: 3600,
    minute: 60,
    second: 1
  };

  for (let key in ranges) {
    if (Math.abs(diffInSeconds) >= ranges[key] || key === 'second') {
      const delta = -Math.round(diffInSeconds / ranges[key]);
      return rtf.format(delta, key);
    }
  }
};

const STOCK_ROUTE_PREFIX = '/stocks';

const renderContentWithStockHighlights = (content, t) => {
  if (!content) return null;

  return content.split(/(\$[A-Z]{1,5}\b)/g).map((segment, index) => {
    const m = segment.match(/^\$([A-Z]{1,5})$/);
    if (m) {
      const symbol = m[1].toUpperCase();
      return (
        <Link
          key={`ct-${index}-${symbol}`}
          to={`${STOCK_ROUTE_PREFIX}/${encodeURIComponent(symbol)}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-700 dark:text-blue-400 text-sm font-semibold rounded-lg px-2 py-0.5 mx-0.5 hover:from-blue-500/30 hover:to-blue-600/30 transition-all duration-200 border border-blue-500/30 shadow-sm"
          title={t('stockers.post.goToSymbol', { symbol })}
        >
          ${symbol}
        </Link>
      );
    }
    return <span key={`txt-${index}`}>{segment}</span>;
  });
};

const getTierColor = (tier) => {
  const lowerTier = (tier || '').toLowerCase();
  if (lowerTier === 'master') return 'from-purple-500 to-purple-600';
  if (lowerTier === 'expert') return 'from-blue-500 to-blue-600';
  if (lowerTier === 'advanced') return 'from-emerald-500 to-emerald-600';
  return 'from-gray-500 to-gray-600';
};

const CredibilityBadge = ({ score, tier }) => {
  const gradient = getTierColor(tier);
  return (
    <div className={`bg-gradient-to-r ${gradient} text-white text-xs px-2 sm:px-3 py-1 rounded-full flex items-center gap-1 sm:gap-1.5 shadow-lg whitespace-nowrap`}>
      <Award size={12} />
      <span className="font-bold">{score}</span>
      <span className="opacity-75 hidden sm:inline">•</span>
      <span className="font-medium hidden sm:inline">{tier}</span>
    </div>
  );
};

const sentimentBtnBase =
  "flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium border-2 transition-all duration-200 select-none touch-manipulation";

const bullActive =
  "bg-gradient-to-r from-emerald-600/30 to-emerald-500/30 text-emerald-300 border-emerald-500/50 shadow-lg shadow-emerald-500/20";

const bullInactive =
  "bg-gray-700/30 text-gray-400 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-emerald-600/10 border-gray-600/50 hover:border-emerald-500/30 active:scale-95";

const bearActive =
  "bg-gradient-to-r from-red-600/30 to-red-500/30 text-red-300 border-red-500/50 shadow-lg shadow-red-500/20";

const bearInactive =
  "bg-gray-700/30 text-gray-400 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-600/10 border-gray-600/50 hover:border-red-500/30 active:scale-95";

const EvidenceItem = ({ evidence, type }) => {
  const { t } = useTranslation();
  const getIcon = () => {
    switch(type) {
      case 'news_article': return <Newspaper className="w-4 h-4 flex-shrink-0" />;
      case 'sec_filing': return <FileText className="w-4 h-4 flex-shrink-0" />;
      default: return <LinkIcon className="w-4 h-4 flex-shrink-0" />;
    }
  };

  const getColor = () => {
    switch(type) {
      case 'news_article': return 'from-blue-600/20 to-blue-500/10 border-blue-500/30 text-blue-400';
      case 'sec_filing': return 'from-purple-600/20 to-purple-500/10 border-purple-500/30 text-purple-400';
      default: return 'from-gray-600/20 to-gray-500/10 border-gray-500/30 text-gray-400';
    }
  };

  const getLabel = () => {
    switch(type) {
      case 'news_article': return t('stockers.post.evidenceLabels.news');
      case 'sec_filing': return t('stockers.post.evidenceLabels.filing');
      default: return t('stockers.post.evidenceLabels.link');
    }
  };

  return (
    <a
      href={evidence.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-start gap-2 sm:gap-3 p-3 bg-gradient-to-r ${getColor()} border rounded-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-lg group`}
    >
      <div className="flex-shrink-0 mt-1">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-wide opacity-75">{getLabel()}</span>
          <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </div>
        <p className="text-sm font-medium text-white line-clamp-2 break-words">{evidence.title}</p>
        {evidence.excerpt && (
          <p className="text-xs text-gray-400 mt-1 line-clamp-2 break-words">{evidence.excerpt}</p>
        )}
        {evidence.relevance_note && (
          <p className="text-xs text-gray-300 mt-2 italic border-l-2 border-current pl-2 line-clamp-2 break-words">
            "{evidence.relevance_note}"
          </p>
        )}
      </div>
    </a>
  );
};

const Post = ({
  post,
  cred,
  isCommentBoxOpen,
  toggleCommentBox,
  onLikeToggle,
  onBullVote,
  onBearVote,
  onCommentSubmit,
  onCommentLikeToggle,
  onViewed,
  onPostDelete,
  onPostReport,
  onCommentDelete,
  onCommentReport
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // MEMOIZED SELECTORS - This fixes the Redux rerender warning
  const currentUser = useSelector((state) => state.profile?.user);
  
  // Create a stable selector for this specific post's evidence
  const evidenceData = useSelector(
    useCallback((state) => {
      const byPost = state.postEvidence?.byPost;
      if (!byPost || !post.id) return [];
      return byPost[post.id] || [];
    }, [post.id])
  );
  
  const evidenceLoading = useSelector((state) => state.postEvidence?.loading || false);
  // Was captured in Redux but never displayed -- a failed evidence fetch
  // used to be indistinguishable from "this post genuinely has no evidence"
  // (the whole section just doesn't render either way). Only shown when
  // there's nothing else to show, so a later successful fetch clears it.
  const evidenceError = useSelector((state) => state.postEvidence?.error || null);

  const isSaved = useSelector((state) => state.savedPosts?.postIds?.includes(post.id) || false);
  const [isBookmarking, setIsBookmarking] = useState(false);

  const handleShare = useCallback(async () => {
    const shareData = { title: post.title, text: post.content, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user canceled */ }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert(t('stockers.post.linkCopied'));
    }
  }, [post.title, post.content, t]);

  const handleBookmarkToggle = useCallback(async () => {
    if (isBookmarking) return;
    setIsBookmarking(true);
    try {
      await dispatch((isSaved ? unsavePostThunk : savePostThunk)({ postId: post.id })).unwrap();
    } catch (err) {
      console.warn('[Post] Bookmark toggle failed:', err);
    } finally {
      setIsBookmarking(false);
    }
  }, [dispatch, isSaved, isBookmarking, post.id]);

  const [showAllComments, setShowAllComments] = useState(false);
  const [showEvidence, setShowEvidence] = useState(true);
  const [showReceipts, setShowReceipts] = useState(false);
  const [evidenceFetched, setEvidenceFetched] = useState(false);
  
  // Local state for vote buttons to prevent glitchiness
  const [localBullVote, setLocalBullVote] = useState(post.has_bull_vote || false);
  const [localBearVote, setLocalBearVote] = useState(post.has_bear_vote || false);
  const [isVoting, setIsVoting] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  // Which vote button (if any) should play the pop-in animation right now --
  // set only on a fresh activation (bull/bear going from off to on), never
  // on deactivation, cleared via onAnimationEnd so it can re-fire next time.
  const [poppedButton, setPoppedButton] = useState(null);
  const [justLiked, setJustLiked] = useState(false);

  // Sync local vote state with props when they change
  useEffect(() => {
    setLocalBullVote(post.has_bull_vote || false);
    setLocalBearVote(post.has_bear_vote || false);
  }, [post.has_bull_vote, post.has_bear_vote]);

  // Fetch evidence when component mounts or when evidence section is expanded
  useEffect(() => {
    if (showEvidence && !evidenceFetched && post.id) {
      dispatch(listEvidenceThunk({ post_id: post.id }));
      setEvidenceFetched(true);
    }
  }, [showEvidence, evidenceFetched, post.id, dispatch]);

  // Memoize evidence array
  const evidence = useMemo(() => {
    return Array.isArray(evidenceData) ? evidenceData : [];
  }, [evidenceData]);
  
  const hasEvidence = evidence.length > 0;

  // Memoize comments array
  const comments = useMemo(() => {
    return Array.isArray(post.comments) ? post.comments : [];
  }, [post.comments]);
  
  const commentsToShow = showAllComments ? comments : comments.slice(0, 3);
  const hasMoreComments = comments.length > 3;

  const ref = useRef();
  useEffect(() => {
    if (!onViewed) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onViewed(post.id);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [onViewed, post.id]);

  // Memoize formatted date
  const formattedDate = useMemo(() => {
    return post.created_at
      ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
      : t('stockers.post.recently');
  }, [post.created_at, t]);

  const isOwner = currentUser?.id === post.user_id || currentUser?.id === post.author_id || currentUser?.id === post.user?.id;

  const handlePostDelete = useCallback(() => {
    if (window.confirm(t('stockers.post.deleteConfirm'))) {
      onPostDelete?.(post.id);
    }
  }, [onPostDelete, post.id, t]);

  const handlePostReport = useCallback(() => {
    onPostReport?.(post.id);
  }, [onPostReport, post.id]);

  // Memoize credibility data
  const { credibilityScore, credibilityTier } = useMemo(() => {
    const credibilityData = typeof post.author_credibility === 'object' 
      ? post.author_credibility 
      : null;
    
    return {
      credibilityScore: credibilityData?.score || post.author_credibility || null,
      credibilityTier: credibilityData?.tier || post.author_tier || 'Beginner'
    };
  }, [post.author_credibility, post.author_tier]);

  // Memoize vote consensus calculations
  const voteConsensus = useMemo(() => {
    const totalVotes = (post.bull_count || 0) + (post.bear_count || 0);
    const bullWeight = post.vote_consensus?.bull_weight || 0;
    const bearWeight = post.vote_consensus?.bear_weight || 0;
    const totalWeight = bullWeight + bearWeight;
    const bullWeightPercent = totalWeight > 0 ? (bullWeight / totalWeight) * 100 : 50;
    const bearWeightPercent = totalWeight > 0 ? (bearWeight / totalWeight) * 100 : 50;

    const consensus = post.vote_consensus?.consensus || 'Neutral';
    const confidence = post.vote_consensus?.confidence || 0;
    const bull_pct = post.vote_consensus?.bull_pct || 0;
    const bear_pct = post.vote_consensus?.bear_pct || 0;

    return {
      totalVotes,
      bullWeightPercent,
      bearWeightPercent,
      consensus,
      confidence,
      bull_pct,
      bear_pct
    };
  }, [post.bull_count, post.bear_count, post.vote_consensus]);

  const getConsensusColor = useCallback(() => {
    if (voteConsensus.consensus === 'Strong Bull' || voteConsensus.consensus === 'Bull') return 'text-emerald-400';
    if (voteConsensus.consensus === 'Strong Bear' || voteConsensus.consensus === 'Bear') return 'text-red-400';
    return 'text-gray-400';
  }, [voteConsensus.consensus]);

  // Memoize chips array
  const chips = useMemo(() => {
    const chipsArray = [];
    if (post.has_filing) chipsArray.push('secFiling');
    if (post.has_news) chipsArray.push('breakingNews');
    if (post.technical_analysis) chipsArray.push('technicalAnalysis');
    if (post.earnings_catalyst) chipsArray.push('earningsCatalyst');
    if (post.institutional_flow) chipsArray.push('institutionalFlow');
    return chipsArray;
  }, [post.has_filing, post.has_news, post.technical_analysis, post.earnings_catalyst, post.institutional_flow]);

  // Memoize receipts
  const receipts = useMemo(() => ({
    news: post.news_drivers || [],
    filing: post.filing_summary || null,
    sentimentChange: post.sentiment_change || null
  }), [post.news_drivers, post.filing_summary, post.sentiment_change]);

  const hasReceipts = receipts.news.length > 0 || receipts.filing || receipts.sentimentChange;

  // Vote handlers. The fixed 300ms setTimeout debounce that used to sit in
  // `finally` here was cosmetic, not tied to real request duration -- it
  // existed to paper over onBullVote/onBearVote resolving instantly
  // regardless of the actual dispatch's outcome (see Posts.jsx, now fixed).
  // Now that the promise genuinely reflects the real request, isVoting
  // clears the moment it actually settles, and the catch below is no
  // longer dead code -- it fires on real failures.
  const handleBullVote = useCallback(async () => {
    if (isVoting) return;

    setIsVoting(true);
    const previousBullVote = localBullVote;
    const previousBearVote = localBearVote;

    // Optimistically update UI
    setLocalBullVote(!previousBullVote);
    if (!previousBullVote) setPoppedButton('bull');
    if (previousBearVote) {
      setLocalBearVote(false);
    }

    try {
      await onBullVote?.(post.id, previousBullVote);
    } catch (error) {
      console.error('Error voting bull:', error);
      // Revert on error
      setLocalBullVote(previousBullVote);
      setLocalBearVote(previousBearVote);
    } finally {
      setIsVoting(false);
    }
  }, [isVoting, localBullVote, localBearVote, onBullVote, post.id]);

  const handleBearVote = useCallback(async () => {
    if (isVoting) return;

    setIsVoting(true);
    const previousBullVote = localBullVote;
    const previousBearVote = localBearVote;

    // Optimistically update UI
    setLocalBearVote(!previousBearVote);
    if (!previousBearVote) setPoppedButton('bear');
    if (previousBullVote) {
      setLocalBullVote(false);
    }

    try {
      await onBearVote?.(post.id, previousBearVote);
    } catch (error) {
      console.error('Error voting bear:', error);
      // Revert on error
      setLocalBullVote(previousBullVote);
      setLocalBearVote(previousBearVote);
    } finally {
      setIsVoting(false);
    }
  }, [isVoting, localBullVote, localBearVote, onBearVote, post.id]);

  // The Like button had no in-flight guard at all (unlike Bull/Bear above)
  // -- a rapid double-click could fire onLikeToggle twice before the first
  // settled. Same pattern as the vote handlers, applied here for the
  // first time.
  const handleLikeClick = useCallback(async () => {
    if (isLiking) return;
    setIsLiking(true);
    if (!post.is_liked) setJustLiked(true);
    try {
      await onLikeToggle(post.id, post.is_liked);
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  }, [isLiking, onLikeToggle, post.id, post.is_liked]);

  return (
    <article
      ref={ref}
      className="relative w-full overflow-hidden bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-4 sm:p-5 shadow-2xl min-h-[260px] hover:border-blue-500/30 hover:shadow-blue-500/10 transition-all duration-300 mb-4 sm:mb-6"
    >
      {/* Header */}
      <header className="flex items-start justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Link to={buildProfilePath(post)} className="block flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold overflow-hidden ring-2 ring-gray-700/50 hover:ring-blue-500/50 transition-all">
              {post.profile_picture || post.user?.profile_picture ? (
                <img
                  src={resolveMediaUrl(post.profile_picture || post.user?.profile_picture)}
                  alt={t('stockers.post.usersProfileAlt', { username: post.username || post.user?.username || t('stockers.post.defaultUsername') })}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-base sm:text-lg">{(post.username || post.user?.username || 'U')[0].toUpperCase()}</span>
              )}
            </div>
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={buildProfilePath(post)} className="font-semibold text-white hover:text-blue-400 transition-colors truncate text-sm sm:text-base">
                {post.username || post.user?.username || t('stockers.post.anonymous')}
              </Link>
              {credibilityScore && (
                <CredibilityBadge 
                  score={credibilityScore} 
                  tier={credibilityTier} 
                />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-gray-500">{formattedDate}</span>
              {typeof post.view_count === 'number' && (
                <>
                  <span className="text-xs text-gray-600">•</span>
                  <span className="flex items-center space-x-1 text-xs text-gray-500">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{post.view_count.toLocaleString()}</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={handleShare}
            title={t('stockers.post.share')}
            className="p-1.5 sm:p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-blue-700 dark:hover:text-blue-400"
          >
            <Share2 size={16} className="sm:w-[18px] sm:h-[18px]" />
          </button>
          <button
            onClick={handleBookmarkToggle}
            disabled={isBookmarking}
            title={isSaved ? t('stockers.post.unsave') : t('stockers.post.save')}
            className={`p-1.5 sm:p-2 hover:bg-gray-700/50 rounded-lg transition-colors disabled:opacity-50 ${isSaved ? 'text-purple-700 dark:text-purple-400' : 'text-gray-400 hover:text-purple-700 dark:hover:text-purple-400'}`}
          >
            <Bookmark size={16} className="sm:w-[18px] sm:h-[18px]" fill={isSaved ? 'currentColor' : 'none'} />
          </button>
          <ActionDropdown
            isOwner={isOwner}
            allowEdit={true}
            allowDelete={true}
            allowReport={true}
            onEdit={() => console.log('Edit post:', post.id)}
            onDelete={handlePostDelete}
            onReport={handlePostReport}
          />
        </div>
      </header>

      {/* Stock Tags */}
      {Array.isArray(post.stocks) && post.stocks.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.stocks.map((s) => {
            const symbol = (s.symbol || '').toUpperCase();
            return (
              <Link
                key={s.id || symbol}
                to={`${STOCK_ROUTE_PREFIX}/${encodeURIComponent(symbol)}`}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-700 dark:text-blue-400 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-500/30 hover:to-blue-600/30 transition-all duration-200 border border-blue-500/30 shadow-sm"
              >
                ${symbol}
              </Link>
            );
          })}
        </div>
      )}

      {/* Title */}
      {post.title && (
        <h2 className="text-lg sm:text-xl font-bold text-white mb-3 leading-tight break-words">
          {post.title}
        </h2>
      )}

      {/* Content */}
      <div className="text-gray-300 leading-relaxed mb-4 text-sm sm:text-[15px] break-words">
        {renderContentWithStockHighlights(post.content, t)}
      </div>

      {/* Position disclosure -- MAR Art 20 requires this alongside the
          recommendation itself, not just captured at submission. */}
      {post.discloses_position && (
        <div className="flex items-center gap-1.5 mb-4 -mt-2 text-xs text-amber-300/90">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
          {t('stockers.post.positionDisclosure')}
        </div>
      )}

      {/* Image */}
      {post.image_url && (
        <div className="mb-4 rounded-xl overflow-hidden border border-gray-700/50">
          <img
            src={post.image_url}
            alt={t('stockers.post.attachmentAlt')}
            className="w-full h-auto max-h-[400px] sm:max-h-[500px] object-cover"
          />
        </div>
      )}

      {/* Category Chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {chips.map(chip => (
            <span key={chip} className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 text-purple-700 dark:text-purple-400 px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium border border-purple-500/30">
              {t(`stockers.post.chips.${chip}`)}
            </span>
          ))}
        </div>
      )}

      {/* Evidence Section */}
      {(hasEvidence || evidenceLoading || (evidenceError && evidenceFetched)) && (
        <div className="mb-4">
          <button 
            onClick={() => setShowEvidence(!showEvidence)}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium mb-3 group"
          >
            <FileText size={16} className="group-hover:scale-110 transition-transform flex-shrink-0" />
            <span>{t('stockers.post.evidenceHeader', { count: evidence.length })}</span>
            {showEvidence ? <ChevronUp size={16} className="flex-shrink-0" /> : <ChevronDown size={16} className="flex-shrink-0" />}
          </button>

          {showEvidence && (
            <div className="space-y-2">
              {evidenceLoading && !hasEvidence && (
                <div className="text-sm text-gray-400 italic">
                  {t('stockers.post.loadingEvidence')}
                </div>
              )}

              {!evidenceLoading && !hasEvidence && evidenceError && (
                <div className="text-sm text-rose-400">
                  {t('stockers.post.evidenceLoadError')}
                </div>
              )}

              {evidence.map((item) => (
                <EvidenceItem
                  key={item.id}
                  evidence={item}
                  type={item.evidence_type}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vote Consensus (layout-stable) */}
      <div className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-xl border border-gray-700/50 min-h-[80px]">
        {voteConsensus.totalVotes > 0 ? (
          <>
            <div className="flex justify-between items-center text-xs text-gray-400 mb-2 gap-2">
              <span className="flex items-center gap-1.5 font-medium whitespace-nowrap">
                <TrendingUp size={14} className="text-emerald-400 flex-shrink-0" />
                <span className="hidden sm:inline">{t('stockers.post.bull')}</span>
                <span className="w-6 text-center inline-block">{post.bull_count || 0}</span>
              </span>

              <span className={`font-bold text-xs sm:text-sm ${getConsensusColor()} text-center`}>
                {voteConsensus.consensus} {voteConsensus.confidence > 0 && `(${voteConsensus.confidence}%)`}
              </span>

              <span className="flex items-center gap-1.5 font-medium whitespace-nowrap">
                <TrendingDown size={14} className="text-red-400 flex-shrink-0" />
                <span className="hidden sm:inline">{t('stockers.post.bear')}</span>
                <span className="w-6 text-center inline-block">{post.bear_count || 0}</span>
              </span>
            </div>

            <div className="h-2.5 bg-gray-900 rounded-full overflow-hidden flex shadow-inner">
              <div
                className="bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-300"
                style={{ width: `${voteConsensus.bullWeightPercent}%` }}
              />
              <div
                className="bg-gradient-to-r from-red-500 to-red-400 transition-all duration-300"
                style={{ width: `${voteConsensus.bearWeightPercent}%` }}
              />
            </div>

            {voteConsensus.bull_pct > 0 && voteConsensus.bear_pct > 0 && (
              <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                <span>{voteConsensus.bull_pct}% {t('stockers.post.bull')}</span>
                <span>{voteConsensus.bear_pct}% {t('stockers.post.bear')}</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center h-full text-xs text-gray-500 italic">
            {t('stockers.post.noVotesYet')}
          </div>
        )}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-4 border-t border-gray-700/50 mb-4 gap-3">
        <div className="flex gap-4 sm:gap-6 text-sm text-gray-400">
          <button
            onClick={handleLikeClick}
            disabled={isLiking}
            className={`flex items-center gap-2 transition-colors duration-200 ${
              post.is_liked ? 'text-purple-400' : 'hover:text-purple-400'
            } ${isLiking ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg
              className={`w-5 h-5 flex-shrink-0 ${justLiked ? 'animate-pop' : ''}`}
              fill={post.is_liked ? "currentColor" : "none"}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              onAnimationEnd={() => setJustLiked(false)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span className="font-medium">{post.likes || 0}</span>
          </button>
          <button
            onClick={() => toggleCommentBox(post.id)}
            className="flex items-center gap-2 hover:text-blue-400 transition-all duration-200"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="font-medium">{comments.length}</span>
          </button>
        </div>
        
        <div className="h-10 flex items-center gap-3">
          <button
            onClick={handleBullVote}
            disabled={isVoting}
            onAnimationEnd={() => setPoppedButton(null)}
            className={`${sentimentBtnBase} ${
              localBullVote ? bullActive : bullInactive
            } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''} ${
              poppedButton === 'bull' ? 'animate-pop' : ''
            } flex-1 sm:flex-none justify-center`}
          >
            <TrendingUp size={16} className="flex-shrink-0" />
            <span>{t('stockers.post.bull')}</span>
          </button>

          <button
            onClick={handleBearVote}
            disabled={isVoting}
            onAnimationEnd={() => setPoppedButton(null)}
            className={`${sentimentBtnBase} ${
              localBearVote ? bearActive : bearInactive
            } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''} ${
              poppedButton === 'bear' ? 'animate-pop' : ''
            } flex-1 sm:flex-none justify-center`}
          >
            <TrendingDown size={16} className="flex-shrink-0" />
            <span>{t('stockers.post.bear')}</span>
          </button>
        </div>
      </div>

      {/* Legacy Receipts */}
      {hasReceipts && (
        <div className="mb-4">
          <button 
            onClick={() => setShowReceipts(!showReceipts)}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-300 transition-colors text-sm font-medium"
          >
            <Activity size={14} className="flex-shrink-0" />
            {t('stockers.post.additionalContext')} {showReceipts ? <ChevronUp size={16} className="flex-shrink-0" /> : <ChevronDown size={16} className="flex-shrink-0" />}
          </button>

          {showReceipts && (
            <div className="mt-3 pt-3 border-t border-gray-700/50 space-y-3">
              {receipts.news.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1 font-medium">
                    <Activity size={12} className="flex-shrink-0" />
                    {t('stockers.post.topNewsDrivers')}
                  </div>
                  {receipts.news.map((news, idx) => (
                    <div key={idx} className="text-sm text-gray-300 flex items-center gap-2 mb-1 break-words">
                      <ExternalLink size={12} className="text-gray-500 flex-shrink-0" />
                      <span className="break-words">{news}</span>
                    </div>
                  ))}
                </div>
              )}
              {receipts.filing && (
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1 font-medium">
                    <FileText size={12} className="flex-shrink-0" />
                    {t('stockers.post.recentFiling')}
                  </div>
                  <div className="text-sm text-gray-300 break-words">{receipts.filing}</div>
                </div>
              )}
              {receipts.sentimentChange && (
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1 font-medium">
                    <AlertTriangle size={12} className="flex-shrink-0" />
                    {t('stockers.post.sentimentTrend')}
                  </div>
                  <div className="text-sm text-emerald-400 font-semibold break-words">{receipts.sentimentChange}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Comment Box */}
      {isCommentBoxOpen && (
        <div className="mb-4">
          <CommentBox 
            postId={post.id} 
            onCommentSubmit={onCommentSubmit}
          />
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div className="space-y-3">
          {commentsToShow.map((c) => (
            <Comment
              key={c.id}
              comment={c}
              onLikeToggle={(commentId, isLiked) => onCommentLikeToggle(commentId, isLiked)}
              onDelete={onCommentDelete}
              onReport={onCommentReport}
            />
          ))}

          {hasMoreComments && (
            <button
              onClick={() => setShowAllComments(!showAllComments)}
              className="text-sm text-purple-400 hover:text-purple-300 font-medium transition-colors duration-200 flex items-center gap-2"
            >
              {showAllComments
                ? t('stockers.post.showFewerComments')
                : t('stockers.post.viewAllComments', { count: comments.length })}
              {showAllComments ? <ChevronUp size={14} className="flex-shrink-0" /> : <ChevronDown size={14} className="flex-shrink-0" />}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default Post;